import React, { useEffect, useState } from "react";
import { X, Copy, RefreshCw, ExternalLink, CheckCircle2, Clock, AlertCircle, Mail, MessageSquare, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PayerSharesBlock } from "./PayerSharesBlock";

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtUSD = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const KIND_LABEL = {
  deposit: "Depósito (30%)",
  balance: "Saldo restante (70%)",
  full: "Pago total (100%)",
  partial: "Pago parcial",
  complete_deposit: "Completar depósito",
};

const STATUS_BADGE = {
  pending: { text: "Pendiente",  cls: "bg-clay-100 text-clay-700",       Icon: Clock },
  created: { text: "Enviado",    cls: "bg-amber-100 text-amber-800",     Icon: Clock },
  approved:{ text: "Aprobado",   cls: "bg-blue-50 text-blue-700",        Icon: Clock },
  captured:{ text: "Pagado",     cls: "bg-pine-soft/40 text-pine",       Icon: CheckCircle2 },
  denied:  { text: "Rechazado",  cls: "bg-red-100 text-red-700",         Icon: AlertCircle },
  refunded:{ text: "Reembolsado",cls: "bg-orange-100 text-orange-700",   Icon: AlertCircle },
  cancelled:{text: "Cancelado",  cls: "bg-clay-200 text-clay-700",       Icon: X },
};

const copyToClipboard = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copiado al portapapeles`);
  } catch {
    toast.error("No se pudo copiar — selecciona y copia manualmente");
  }
};

/** Agent-facing modal: lazy-generate or refresh the public payment link,
 *  show the pre-formatted instructions, and surface the current payment
 *  history (deposit captured? balance pending?) for the trip.
 */
export function PaymentLinkModal({ open, itineraryId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Send the browser's visible origin so the public URL points at the
      // host the agent is actually on (avoids preview/cluster Origin-header
      // weirdness where the ingress rewrites Origin to an internal hostname).
      const { data: d } = await api.post(
        `/itineraries/${itineraryId}/payments/create-link`,
        { origin: window.location.origin },
      );
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el enlace");
    } finally {
      setLoading(false);
    }
  };

  const switchCurrency = async (nextCurrency) => {
    // Persist the new payment_currency on the itinerary and reload the
    // modal so amounts, PayPal-charge preview and email instructions all
    // update to the new currency together. The link URL itself is
    // idempotent (same token) — clients who already have it see the new
    // amount too, which is intentional: the agent is the source of truth
    // for the price they want to charge.
    if (switching || nextCurrency === (data?.payment_currency || "USD")) return;
    setSwitching(true);
    try {
      await api.patch(`/itineraries/${itineraryId}`, { payment_currency: nextCurrency });
      await load();
      toast.success(`Cambiado a ${nextCurrency === "EUR" ? "Euros" : "Dólares"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar la moneda");
    } finally {
      setSwitching(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open, itineraryId]);

  if (!open) return null;
  const options = data?.options || {};
  const payments = data?.payments || [];
  const paymentUrl = data?.payment_url;
  const instructions = data?.instructions || "";

  return (
    <div className="fixed inset-0 z-50 bg-clay-900/50 flex items-center justify-center p-4"
         data-testid="payment-modal-backdrop"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white border border-clay-300 w-full max-w-3xl max-h-[92vh] overflow-auto shadow-xl"
           data-testid="payment-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-clay-300 sticky top-0 bg-white z-10">
          <div>
            <div className="smallcaps text-clay-700">Pago del cliente</div>
            <div className="font-serif text-2xl mt-1">Generar enlace de pago PayPal</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-clay-100" data-testid="payment-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {loading && !data && (
            <div className="text-sm text-clay-700 py-12 text-center">Generando enlace…</div>
          )}

          {data && (
            <>
              {/* Currency toggle — decide whether the client is charged in
                  USD or EUR before generating the link. Persists to the
                  itinerary doc so extras + the /pay/:token page + email
                  all stay consistent. Default: USD (matches the 2026-07-09
                  switchover). Introduced 2026-08-17 at the agent's request. */}
              {(() => {
                const currency = data.payment_currency || "USD";
                const fmt = currency === "EUR" ? fmtEUR : fmtUSD;
                return (
                  <>
                    <div className="border border-clay-300" data-testid="payment-currency-toggle">
                      <div className="px-3 py-2 border-b border-clay-300 bg-clay-50 flex items-center justify-between gap-3">
                        <div className="smallcaps text-clay-700">Moneda del cobro</div>
                        <div className="inline-flex items-center border border-clay-400">
                          {[
                            { code: "USD", label: "USD ($)" },
                            { code: "EUR", label: "EUR (€)" },
                          ].map(({ code, label }) => (
                            <button
                              key={code}
                              type="button"
                              onClick={() => switchCurrency(code)}
                              disabled={switching}
                              data-testid={`payment-currency-${code.toLowerCase()}`}
                              className={`px-3 py-1 text-xs tracking-wider transition ${
                                currency === code
                                  ? "bg-clay-900 text-white"
                                  : "bg-white text-clay-700 hover:bg-clay-100"
                              } ${switching ? "opacity-60 cursor-wait" : ""}`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="px-3 py-2 text-[11px] text-clay-600 bg-white">
                        El cliente verá los importes y pagará en <strong>{currency === "EUR" ? "Euros" : "Dólares"}</strong>.
                        {currency === "EUR" ? " Ideal si el cliente es europeo — evita la comisión de conversión de PayPal." : " Recomendado si el cliente paga desde EE.UU."}
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-0 border border-clay-300">
                      <div className="p-3 border-r border-clay-300">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-clay-700">Total viaje</div>
                        <div className="font-serif text-xl tabular mt-1">{fmt(options.total_eur)}</div>
                      </div>
                      <div className="p-3 border-r border-clay-300">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-clay-700">Pagado</div>
                        <div className="font-serif text-xl tabular mt-1 text-pine">{fmt(options.paid_eur)}</div>
                      </div>
                      <div className="p-3">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-clay-700">Pendiente</div>
                        <div className="font-serif text-xl tabular mt-1 text-terracotta">{fmt(options.remaining_eur)}</div>
                      </div>
                    </div>
                  </>
                );
              })()}

              {/* FX rate not saved warning — only relevant when charging
                  in USD (an EUR charge doesn't need a rate at all). */}
              {(data.payment_currency || "USD") === "USD" && data.fx_rate == null && (
                <div
                  data-testid="payment-link-fx-warning"
                  className="border border-amber-400 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 flex items-start gap-2"
                >
                  <span aria-hidden="true" className="text-base leading-none">⚠</span>
                  <div>
                    <div className="font-semibold">La tasa de cambio no está guardada en este itinerario</div>
                    <div className="text-amber-800 mt-0.5">
                      El enlace usará la tasa ECB del día. Para fijar el precio en dólares y evitar que se mueva si la tasa cambia, cierra este panel, ve al conversor EUR → USD (panel de la derecha) y pulsa <span className="font-semibold">Guardar</span>.
                    </div>
                  </div>
                </div>
              )}

              {/* Rule explanation */}
              <div className="text-xs text-clay-700 bg-clay-50 border border-clay-200 px-3 py-2">
                {options.days_to_trip != null && (
                  <>
                    Quedan <span className="font-semibold">{options.days_to_trip} días</span> para la salida.
                    {options.days_to_trip > 60
                      ? " El cliente puede pagar la reserva (30%) o el total (100%)."
                      : " Sólo está disponible el pago total (≤60 días para la salida)."}
                  </>
                )}
                {options.days_to_trip == null && <>No hay fecha de inicio definida. El cliente solo verá el pago total.</>}
              </div>

              {/* Free-amount from the first payment (per trip) */}
              <label className="flex items-start gap-2 text-xs text-clay-800 border border-clay-200 bg-white px-3 py-2.5 cursor-pointer" data-testid="allow-custom-initial-row">
                <input type="checkbox" className="accent-terracotta mt-0.5" checked={!!data.allow_custom_initial} disabled={switching}
                  data-testid="allow-custom-initial-toggle"
                  onChange={async (e) => {
                    setSwitching(true);
                    try {
                      await api.patch(`/itineraries/${itineraryId}`, { allow_custom_initial: e.target.checked });
                      await load();
                      toast.success(e.target.checked ? "El cliente ya puede pagar cualquier cantidad desde el primer pago" : "Vuelve a la regla normal (reserva 30% o total)");
                    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar"); }
                    finally { setSwitching(false); }
                  }}/>
                <span>
                  <span className="font-semibold">Permitir cualquier cantidad desde el primer pago</span>
                  <span className="block text-clay-600 mt-0.5">
                    Añade la tarjeta «Custom amount» al enlace antes de que haya ningún cobro, con un mínimo simbólico del 1% del total por pago. Útil cuando un grupo quiere pagar la reserva en varios pagos libres.
                  </span>
                </span>
              </label>

              {/* Client trip presentation URL removed 2026-07-10 — the
                  Fora-style presentation view is disabled on the client-
                  facing app, so we no longer surface it to agents. Only
                  the direct payment URL is shared with clients. */}

              {/* Public URL */}
              <div>
                <div className="smallcaps mb-2 flex items-center justify-between">
                  <span>Enlace de pago directo</span>
                  <button onClick={load} disabled={loading}
                          data-testid="payment-link-refresh"
                          title="Refrescar (mismo token, totales actualizados)"
                          className="text-[10px] uppercase tracking-widest text-clay-700 hover:text-terracotta inline-flex items-center gap-1 disabled:opacity-40">
                    <RefreshCw size={11}/> Refrescar
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="payment-link-url"
                    readOnly value={paymentUrl}
                    onClick={(e) => e.target.select()}
                    className="flex-1 bg-clay-50 border border-clay-300 px-3 py-2 text-sm font-mono outline-none"
                  />
                  <button onClick={() => copyToClipboard(paymentUrl, "Enlace")}
                          data-testid="copy-payment-url"
                          className="inline-flex items-center gap-2 px-3 py-2 border border-clay-300 hover:bg-clay-100 text-xs">
                    <Copy size={13}/> Copiar URL
                  </button>
                  <a href={paymentUrl} target="_blank" rel="noreferrer"
                     data-testid="open-payment-url"
                     className="inline-flex items-center gap-2 px-3 py-2 border border-clay-300 hover:bg-clay-100 text-xs">
                    <ExternalLink size={13}/> Abrir
                  </a>
                </div>
              </div>

              {/* Reparto por viajero — custom split with per-share progress */}
              <PayerSharesBlock
                itineraryId={itineraryId}
                currency={data.payment_currency || "USD"}
                total={options.total_eur || 0}
                shares={data.payer_shares || []}
                paymentUrl={paymentUrl}
                onSaved={load}
              />

              {/* Instructions textarea (pre-formatted) */}
              <div>
                <div className="smallcaps mb-2 flex items-center justify-between">
                  <span>Mensaje para el cliente</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => copyToClipboard(instructions, "Mensaje completo")}
                            data-testid="copy-instructions-email"
                            className="inline-flex items-center gap-1 px-2 py-1 border border-clay-300 hover:bg-clay-100 text-[11px]">
                      <Mail size={11}/> Copiar para email
                    </button>
                    <button onClick={() => copyToClipboard(toWhatsApp(instructions), "Mensaje (WhatsApp)")}
                            data-testid="copy-instructions-whatsapp"
                            className="inline-flex items-center gap-1 px-2 py-1 border border-clay-300 hover:bg-clay-100 text-[11px]">
                      <MessageSquare size={11}/> WhatsApp
                    </button>
                  </div>
                </div>
                <textarea
                  data-testid="payment-instructions"
                  readOnly value={instructions}
                  rows={14}
                  onClick={(e) => e.target.select()}
                  className="w-full bg-clay-50 border border-clay-300 px-3 py-2 text-sm font-mono outline-none resize-y leading-relaxed"
                />
                <div className="text-[10px] text-clay-500 mt-1">
                  El mensaje incluye el enlace, el detalle del importe a pagar, y la lista de info que necesitas del cliente.
                </div>
              </div>

              {/* Datos del cliente (formulario rellenado en el enlace público) */}
              <TravelerInfoBlock info={data?.traveler_info} />

              {/* Payment history */}
              <div>
                <div className="smallcaps mb-2">Histórico de pagos ({payments.length})</div>
                {payments.length === 0 ? (
                  <div className="text-xs text-clay-500 italic px-3 py-3 border border-dashed border-clay-300">
                    Aún no se ha intentado ningún pago. Cuando el cliente pulse el enlace y elija una opción, aparecerá aquí.
                  </div>
                ) : (
                  <div className="border border-clay-300">
                    {payments.map((p) => {
                      const badge = STATUS_BADGE[p.status] || STATUS_BADGE.pending;
                      const Icon = badge.Icon;
                      return (
                        <div key={p.payment_id}
                             data-testid={`payment-row-${p.payment_id}`}
                             className="grid grid-cols-[1fr_120px_100px_140px] gap-2 px-3 py-2 border-b border-clay-200 last:border-b-0 text-sm items-center">
                          <div>
                            <div className="font-medium">{KIND_LABEL[p.kind] || p.kind}</div>
                            {(p.payer_name || p.share_label) && (
                              <div className="text-[11px] text-clay-700 mt-0.5" data-testid={`payment-payer-${p.payment_id}`}>
                                {p.payer_name || p.share_label}{p.share_id ? " · reparto" : ""}
                              </div>
                            )}
                            {p.paypal_capture_id && (
                              <div className="text-[10px] text-clay-500 font-mono mt-0.5">PayPal: {p.paypal_capture_id}</div>
                            )}
                            {p.notes && (
                              <div className="text-[11px] text-clay-700 italic mt-0.5">{p.notes}</div>
                            )}
                            {p.tos_accepted_at && (
                              <div className="text-[10px] text-pine mt-0.5"
                                   data-testid={`payment-tos-${p.payment_id}`}
                                   title={`T&C version: ${p.tos_version || "unknown"}${p.tos_accepted_ip ? " · IP " + p.tos_accepted_ip : ""}`}>
                                <ShieldCheck size={9} className="inline mr-0.5"/>
                                T&C aceptados {p.tos_accepted_at.slice(0, 10)}
                                {p.tos_accepted_ip ? <span className="text-clay-500"> · IP {p.tos_accepted_ip}</span> : null}
                              </div>
                            )}
                          </div>
                          <div className="tabular text-right">
                            {(() => {
                              // Historical payments carry the currency they
                              // were captured in on `charge_currency`
                              // (2026-08-17) or `paid_currency` (2026-07-09).
                              // Fall back to the trip's current setting for
                              // legacy rows that predate both fields.
                              const c = (p.charge_currency || p.paid_currency || data.payment_currency || "USD").toUpperCase();
                              return c === "EUR" ? fmtEUR(p.amount_usd ?? p.paid_amount ?? p.amount_eur) : fmtUSD(p.amount_usd ?? p.paid_amount ?? p.amount_eur);
                            })()}
                          </div>
                          <div className="text-[10px] text-clay-500 text-right">
                            {(p.paid_at || p.created_at || "").slice(0, 10)}
                          </div>
                          <div className={`inline-flex items-center gap-1 justify-center px-2 py-0.5 text-[10px] uppercase tracking-widest ${badge.cls}`}>
                            <Icon size={11}/> {badge.text}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Reformat the email-style instructions into a WhatsApp-friendly snippet:
// shorter, less corporate, keeps the link + amount and the data list.
function toWhatsApp(emailText) {
  // Pull just the link, the intro paragraph, and the data list — the
  // travel-document paragraph is verbose for a WhatsApp message.
  const lines = emailText.split("\n");
  const hi = lines[0] || "";
  const link = lines.find((l) => l.startsWith("http")) || "";
  const introPara = lines.slice(2, 6).join(" ").replace(/\s+/g, " ").trim();
  const dataIdx = lines.findIndex((l) => l.startsWith("- Full names"));
  const dataLines = dataIdx >= 0 ? lines.slice(dataIdx, dataIdx + 6).join("\n") : "";
  return `${hi}

${introPara}

${link}

Para confirmar las reservas necesito los siguientes datos:
${dataLines}

¡Avísame si tienes cualquier duda!`;
}

/** Render the booking info the client submitted from the public page.
 *  Hidden when nothing has been submitted yet. */
function TravelerInfoBlock({ info }) {
  if (!info) {
    return (
      <div>
        <div className="smallcaps mb-2">Datos del cliente</div>
        <div className="text-xs text-clay-500 italic px-3 py-3 border border-dashed border-clay-300">
          El cliente todavía no ha rellenado el formulario. Aparecerá aquí cuando lo envíe desde el enlace.
        </div>
      </div>
    );
  }
  const people = info.people || [];
  const fmtTs = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  return (
    <div data-testid="agent-traveler-info">
      <div className="smallcaps mb-2 flex items-center justify-between">
        <span>Datos del cliente</span>
        {info.submitted_at && (
          <span className="text-[10px] text-clay-500 normal-case tracking-normal">
            Enviado · {fmtTs(info.submitted_at)}
          </span>
        )}
      </div>
      <div className="border border-clay-300 divide-y divide-clay-200">
        {people.length > 0 && (
          <div className="px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-clay-700 mb-1">
              Viajeros ({people.length})
            </div>
            <div className="grid gap-1.5">
              {people.map((p, i) => (
                <div key={i} className="grid grid-cols-[1.6fr_1fr_1fr] gap-2 text-sm">
                  <div className="truncate">{p.full_name || <em className="text-clay-500">sin nombre</em>}</div>
                  <div className="font-mono text-xs text-clay-700">{p.passport_number || "—"}</div>
                  <div className="tabular text-xs text-clay-700">{p.date_of_birth || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="px-3 py-2 grid grid-cols-2 gap-2 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-clay-700">Vuelo llegada</div>
            <div>
              {info.arrival_flight
                ? <>{info.arrival_flight}{info.arrival_time ? <> · <span className="tabular">{info.arrival_time}</span></> : null}</>
                : <span className="text-clay-500">—</span>}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-clay-700">Vuelo salida</div>
            <div>
              {info.departure_flight
                ? <>{info.departure_flight}{info.departure_time ? <> · <span className="tabular">{info.departure_time}</span></> : null}</>
                : <span className="text-clay-500">—</span>}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-clay-700">Teléfono</div>
            <div>{info.phone || <span className="text-clay-500">—</span>}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-clay-700">Email</div>
            <div className="truncate">{info.submitted_by_email || <span className="text-clay-500">—</span>}</div>
          </div>
        </div>
        {info.notes && (
          <div className="px-3 py-2 text-sm">
            <div className="text-[10px] uppercase tracking-widest text-clay-700 mb-0.5">Alergias / Notas</div>
            <div className="whitespace-pre-wrap">{info.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
}
